import { useState, useRef, useEffect, useCallback } from 'react';
import { Play, Pause, RotateCcw, Film, ChevronDown } from 'lucide-react';
import { ANIMATION_TEMPLATES, TEMPLATE_MAP } from '@/lib/animation/templates';
import type {
  AnimationInstance, CanvasGroup, CanvasGroupGeometry, AnimationParam,
} from '@/lib/animation/types';

interface Props {
  animations: AnimationInstance[];
  groups: CanvasGroup[];
  getGeometry: (group: CanvasGroup) => CanvasGroupGeometry | null;
  onUpdateAnimations: (animations: AnimationInstance[]) => void;
  onPreviewFrame: (frames: Map<string, string>) => void;
  onPreviewClear: () => void;
}

export function AnimationTimeline({
  animations, groups, getGeometry, onUpdateAnimations,
  onPreviewFrame, onPreviewClear,
}: Props) {
  const [selectedAnimId, setSelectedAnimId] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);

  // Visible preview canvas ref (mounted in the DOM)
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  // Off-screen slice canvas for SVG overlay extraction
  const sliceCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number>(0);
  const startTimeRef = useRef<number>(0);
  const progressRef = useRef<number>(0);
  // Throttle SVG overlay updates (every 4th frame)
  const frameCountRef = useRef(0);

  const selectedAnim = animations.find((a) => a.id === selectedAnimId);
  const selectedGroup = selectedAnim ? groups.find((g) => g.id === selectedAnim.canvasGroupId) : null;
  const template = selectedAnim ? TEMPLATE_MAP[selectedAnim.templateId] : null;
  const geometry = selectedGroup ? getGeometry(selectedGroup) : null;

  // Refs for latest values (so renderFrame never triggers re-render loops)
  const templateRef = useRef(template);
  const geometryRef = useRef(geometry);
  const selectedAnimRef = useRef(selectedAnim);
  const onPreviewFrameRef = useRef(onPreviewFrame);
  const onPreviewClearRef = useRef(onPreviewClear);
  templateRef.current = template;
  geometryRef.current = geometry;
  selectedAnimRef.current = selectedAnim;
  onPreviewFrameRef.current = onPreviewFrame;
  onPreviewClearRef.current = onPreviewClear;

  // ── Animation CRUD ────────────────────────────────────────────────────
  const addAnimation = (templateId: string, groupId: string) => {
    const tmpl = TEMPLATE_MAP[templateId];
    if (!tmpl) return;
    const id = `anim-${Date.now()}`;
    const params: Record<string, any> = {};
    for (const p of tmpl.params) params[p.key] = p.default;
    const inst: AnimationInstance = {
      id, templateId, canvasGroupId: groupId,
      name: tmpl.name, durationSeconds: 5, params,
    };
    onUpdateAnimations([...animations, inst]);
    setSelectedAnimId(id);
    setShowTemplatePicker(false);
    setProgress(0);
    setPlaying(false);
  };

  const updateAnim = (patch: Partial<AnimationInstance>) => {
    if (!selectedAnim) return;
    onUpdateAnimations(animations.map((a) =>
      a.id === selectedAnim.id ? { ...a, ...patch } : a
    ));
  };

  const deleteAnim = (id: string) => {
    onUpdateAnimations(animations.filter((a) => a.id !== id));
    if (selectedAnimId === id) { setSelectedAnimId(null); setPlaying(false); onPreviewClear(); }
  };

  // ── Render frame ──────────────────────────────────────────────────────
  // Draws directly to the visible preview canvas (fast, no data URL overhead).
  // Every Nth frame, also extracts low-res JPEG slices for the SVG overlay.
  const renderFrame = useCallback((prog: number) => {
    const tmpl = templateRef.current;
    const geo = geometryRef.current;
    const anim = selectedAnimRef.current;
    const canvas = previewCanvasRef.current;
    if (!tmpl || !geo || !anim || !canvas) return;

    // Size the visible canvas to fit the panel (max 960px wide, maintain aspect)
    const aspect = geo.renderWidth / geo.renderHeight;
    const maxW = Math.min(960, canvas.parentElement?.clientWidth ?? 960);
    const cw = Math.round(maxW);
    const ch = Math.round(maxW / aspect);
    if (canvas.width !== cw || canvas.height !== ch) {
      canvas.width = cw;
      canvas.height = ch;
    }

    const ctx = canvas.getContext('2d')!;
    ctx.clearRect(0, 0, cw, ch);

    // Scale from virtual render coords to canvas pixel coords
    const sx = cw / geo.renderWidth;
    const sy = ch / geo.renderHeight;
    ctx.save();
    ctx.scale(sx, sy);
    tmpl.render(ctx, geo.renderWidth, geo.renderHeight, prog, anim.params);
    ctx.restore();

    // Draw slice boundaries
    ctx.save();
    ctx.strokeStyle = 'rgba(255,255,255,0.15)';
    ctx.lineWidth = 1;
    for (const slice of geo.slices) {
      ctx.strokeRect(
        slice.x * sx, slice.y * sy,
        slice.w * sx, slice.h * sy,
      );
    }
    ctx.restore();

    // Throttle SVG overlay: every 4th frame extract low-res JPEG slices
    frameCountRef.current++;
    if (frameCountRef.current % 4 !== 0) return;

    let sliceCanvas = sliceCanvasRef.current;
    if (!sliceCanvas) {
      sliceCanvas = document.createElement('canvas');
      sliceCanvasRef.current = sliceCanvas;
    }
    const sliceCtx = sliceCanvas.getContext('2d')!;
    const frameMap = new Map<string, string>();

    for (const slice of geo.slices) {
      // Low-res: cap each slice at 480px wide
      const sliceScale = Math.min(1, 480 / slice.w);
      const dw = Math.round(slice.w * sliceScale);
      const dh = Math.round(slice.h * sliceScale);
      sliceCanvas.width = dw;
      sliceCanvas.height = dh;
      sliceCtx.clearRect(0, 0, dw, dh);

      // Source from the visible preview canvas
      const srcX = slice.x * sx;
      const srcY = slice.y * sy;
      const srcW = slice.w * sx;
      const srcH = slice.h * sy;
      sliceCtx.drawImage(canvas, srcX, srcY, srcW, srcH, 0, 0, dw, dh);

      const key = `${slice.monitorRef.wallId}:${slice.monitorRef.monitorId}`;
      frameMap.set(key, sliceCanvas.toDataURL('image/jpeg', 0.6));
    }

    onPreviewFrameRef.current(frameMap);
  }, []);

  // ── Playback loop ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!playing || !selectedAnim) return;
    const duration = selectedAnim.durationSeconds * 1000;
    startTimeRef.current = performance.now() - progressRef.current * duration;

    const tick = (now: number) => {
      const elapsed = now - startTimeRef.current;
      const p = (elapsed / duration) % 1;
      progressRef.current = p;
      setProgress(p);
      renderFrame(p);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [playing, selectedAnim?.id, selectedAnim?.durationSeconds, renderFrame]);

  // Re-render current frame when params or geometry change (not playing)
  const paramsJson = selectedAnim ? JSON.stringify(selectedAnim.params) : '';
  const geoKey = geometry ? `${geometry.renderWidth}x${geometry.renderHeight}` : '';
  useEffect(() => {
    if (!playing && selectedAnim) {
      renderFrame(progressRef.current);
    }
  }, [paramsJson, geoKey, selectedAnim?.id, selectedAnim?.templateId, playing, renderFrame]);

  // Clear preview on unmount
  useEffect(() => {
    return () => onPreviewClearRef.current();
  }, []);

  // ── No groups yet ─────────────────────────────────────────────────────
  if (groups.length === 0) {
    return (
      <div className="border-t border-slate-800 bg-slate-900 px-4 py-4">
        <div className="flex items-center gap-2 mb-2">
          <Film size={14} className="text-slate-500" />
          <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Animation</span>
          <div className="flex-1 h-px bg-slate-800" />
        </div>
        <p className="text-xs text-slate-500 italic">Create a canvas group first to design animations.</p>
      </div>
    );
  }

  // ── Param controls ────────────────────────────────────────────────────
  const renderParamControl = (param: AnimationParam) => {
    const val = selectedAnim?.params[param.key] ?? param.default;
    const onChange = (v: any) => {
      if (!selectedAnim) return;
      updateAnim({ params: { ...selectedAnim.params, [param.key]: v } });
    };

    if (param.type === 'number') {
      return (
        <div key={param.key} className="flex items-center gap-2">
          <label className="text-[10px] font-mono text-slate-500 w-20 shrink-0 truncate">{param.label}</label>
          <input type="range" min={param.min} max={param.max} step={param.step}
            value={val as number} onChange={(e) => onChange(parseFloat(e.target.value))}
            className="flex-1 h-1 accent-cyan-500" />
          <span className="text-[10px] font-mono text-slate-400 w-10 text-right">{val}</span>
        </div>
      );
    }
    if (param.type === 'color') {
      return (
        <div key={param.key} className="flex items-center gap-2">
          <label className="text-[10px] font-mono text-slate-500 w-20 shrink-0 truncate">{param.label}</label>
          <input type="color" value={val as string} onChange={(e) => onChange(e.target.value)}
            className="w-6 h-6 rounded border border-slate-700 bg-transparent cursor-pointer" />
          <span className="text-[10px] font-mono text-slate-400">{val}</span>
        </div>
      );
    }
    if (param.type === 'select') {
      return (
        <div key={param.key} className="flex items-center gap-2">
          <label className="text-[10px] font-mono text-slate-500 w-20 shrink-0 truncate">{param.label}</label>
          <select value={val as string} onChange={(e) => onChange(e.target.value)}
            className="flex-1 bg-slate-950 border border-slate-700 rounded px-2 py-0.5 text-xs text-slate-200 font-mono">
            {param.options?.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      );
    }
    if (param.type === 'boolean') {
      return (
        <div key={param.key} className="flex items-center gap-2">
          <label className="text-[10px] font-mono text-slate-500 w-20 shrink-0 truncate">{param.label}</label>
          <input type="checkbox" checked={val as boolean} onChange={(e) => onChange(e.target.checked)}
            className="accent-cyan-500" />
        </div>
      );
    }
    return null;
  };

  return (
    <div className="border-t border-slate-800 bg-slate-900 px-4 py-3">
      <div className="flex items-center gap-2 mb-3">
        <Film size={14} className="text-cyan-500" />
        <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Animation Designer</span>
        <div className="flex-1 h-px bg-slate-800" />

        {/* Animation selector */}
        {animations.length > 0 && (
          <select value={selectedAnimId ?? ''} onChange={(e) => {
            setSelectedAnimId(e.target.value || null);
            setPlaying(false);
            setProgress(0);
            if (!e.target.value) onPreviewClear();
          }}
            className="bg-slate-950 border border-slate-700 rounded px-2 py-1 text-xs text-slate-200 font-mono">
            <option value="">Select animation...</option>
            {animations.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        )}

        <button onClick={() => setShowTemplatePicker(!showTemplatePicker)}
          className="px-2 py-1 text-xs bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded font-medium transition flex items-center gap-1">
          + New <ChevronDown size={10} />
        </button>
      </div>

      {/* Template picker dropdown */}
      {showTemplatePicker && (
        <div className="mb-3 bg-slate-950 border border-slate-700 rounded shadow-xl max-h-64 overflow-y-auto">
          {groups.length === 0 ? (
            <div className="px-3 py-2 text-xs text-slate-500 italic">No canvas groups available</div>
          ) : (
            <>
              <div className="px-3 py-1.5 text-[10px] font-mono text-slate-500 uppercase tracking-wider bg-slate-900 sticky top-0 border-b border-slate-800">
                Choose template &amp; group
              </div>
              {ANIMATION_TEMPLATES.map((tmpl) => (
                <div key={tmpl.id} className="border-b border-slate-800/50 last:border-0">
                  <div className="px-3 py-1.5 flex items-center gap-2">
                    <span className="text-xs text-slate-200 font-medium flex-1">{tmpl.name}</span>
                    <span className="text-[10px] font-mono text-slate-600">{tmpl.category}</span>
                  </div>
                  <div className="px-3 pb-1.5 flex gap-1.5 flex-wrap">
                    {groups.map((g) => (
                      <button key={g.id} onClick={() => addAnimation(tmpl.id, g.id)}
                        className="px-2 py-0.5 text-[10px] bg-slate-800 hover:bg-cyan-900 border border-slate-700 hover:border-cyan-600 rounded font-mono transition">
                        {g.name}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              <button onClick={() => setShowTemplatePicker(false)}
                className="w-full px-3 py-1.5 text-xs text-slate-500 hover:text-slate-300 border-t border-slate-800">
                Cancel
              </button>
            </>
          )}
        </div>
      )}

      {/* Selected animation editor */}
      {selectedAnim && template && (
        <div className="space-y-3">
          {/* Preview canvas — renders directly, no data URL overhead */}
          <div className="bg-black rounded overflow-hidden border border-slate-800">
            <canvas ref={previewCanvasRef} className="w-full" style={{ height: 120, objectFit: 'contain' }} />
          </div>

          <div className="flex gap-4">
            {/* Left: params */}
            <div className="flex-1 min-w-0 space-y-2">
              <div className="flex items-center gap-2 mb-1">
                <input type="text" value={selectedAnim.name}
                  onChange={(e) => updateAnim({ name: e.target.value })}
                  className="bg-transparent border-b border-slate-700 focus:border-cyan-500 text-sm text-slate-200 font-medium outline-none px-0 py-0.5 flex-1 min-w-0" />
                <select value={selectedAnim.canvasGroupId}
                  onChange={(e) => updateAnim({ canvasGroupId: e.target.value })}
                  className="bg-slate-950 border border-slate-700 rounded px-2 py-0.5 text-[10px] text-slate-300 font-mono">
                  {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
                <button onClick={() => deleteAnim(selectedAnim.id)}
                  className="text-slate-600 hover:text-red-400 text-xs px-1.5 py-0.5 transition">&times;</button>
              </div>

              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
                {template.params.map(renderParamControl)}
                <div className="flex items-center gap-2">
                  <label className="text-[10px] font-mono text-slate-500 w-20 shrink-0">Duration</label>
                  <input type="range" min={1} max={30} step={0.5}
                    value={selectedAnim.durationSeconds}
                    onChange={(e) => updateAnim({ durationSeconds: parseFloat(e.target.value) })}
                    className="flex-1 h-1 accent-cyan-500" />
                  <span className="text-[10px] font-mono text-slate-400 w-10 text-right">{selectedAnim.durationSeconds}s</span>
                </div>
              </div>
            </div>

            {/* Right: playback controls + scrubber */}
            <div className="w-64 shrink-0 flex flex-col justify-center gap-2">
              {/* Transport */}
              <div className="flex items-center gap-2 justify-center">
                <button onClick={() => { setProgress(0); progressRef.current = 0; renderFrame(0); }}
                  className="p-1.5 text-slate-500 hover:text-slate-200 bg-slate-800 hover:bg-slate-700 rounded transition"
                  title="Reset">
                  <RotateCcw size={14} />
                </button>
                <button onClick={() => setPlaying(!playing)}
                  className={`p-2 rounded transition ${playing
                    ? 'bg-cyan-600 hover:bg-cyan-500 text-white'
                    : 'bg-slate-800 hover:bg-slate-700 text-slate-300'}`}
                  title={playing ? 'Pause' : 'Play'}>
                  {playing ? <Pause size={16} /> : <Play size={16} />}
                </button>
              </div>

              {/* Scrubber */}
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-mono text-slate-500 w-8 text-right">
                  {(progress * selectedAnim.durationSeconds).toFixed(1)}s
                </span>
                <input type="range" min={0} max={1} step={0.001}
                  value={progress}
                  onChange={(e) => {
                    const p = parseFloat(e.target.value);
                    setProgress(p);
                    progressRef.current = p;
                    if (playing) {
                      startTimeRef.current = performance.now() - p * selectedAnim.durationSeconds * 1000;
                    } else {
                      renderFrame(p);
                    }
                  }}
                  className="flex-1 h-1 accent-cyan-500" />
                <span className="text-[10px] font-mono text-slate-500 w-8">
                  {selectedAnim.durationSeconds.toFixed(1)}s
                </span>
              </div>

              {/* Geometry info */}
              {geometry && (
                <div className="text-center text-[10px] font-mono text-slate-600">
                  {geometry.renderWidth.toLocaleString()} &times; {geometry.renderHeight.toLocaleString()} px
                  &middot; {geometry.slices.length} monitors
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
